/**
 * Los ficheros de certificación MITECO que la consola está viendo, y cuándo se leyeron de Drive.
 *
 * PARA QUÉ EXISTE (pedido por Javi el 2026-09-17): Ops sube ficheros .xlsx al Drive y necesita
 * comprobar, desde la consola, que el pipeline ya los ha cogido. El 2026-09-15 pasó lo contrario:
 * 16 ficheros de Castilla y León entraron en Drive a las 09:00 y la tabla de la consola se había
 * construido a las 07:49, así que no salían en el desplegable y nadie sabía si era un fallo o un
 * desfase. Con esto en pantalla, esa pregunta tiene respuesta sin preguntar a Data.
 *
 * QUÉ ENSEÑA, Y QUÉ NO:
 *   - Solo ficheros con viajes AÚN pagables (es la misma tabla `int_caes_console_elegibles`, con su
 *     filtro `BLOCKED_BY_CERTIFICATION`). Un fichero cuyos viajes ya se pagaron todos desaparece de
 *     aquí igual que del filtro. Decidido así por Javi: la lista es "lo que la consola pinta".
 *   - Es del UNIVERSO COMPLETO, no del filtro aplicado. Si Ops filtra por Andalucía y esta lista
 *     solo trajera ficheros de Andalucía, leería "el pipeline no ha cogido los de Castilla y León"
 *     cuando sí lo hizo. Por eso NO sale del preview: es una consulta aparte, de solo lectura, sin
 *     snapshot ni audit log, que se carga al abrir la pestaña.
 *   - NO es un filtro. Nada de esta lista se puede clicar para acotar: filtrar es una sola cosa, en
 *     un solo sitio (los desplegables), desde el 2026-09-10 (BB-2000).
 *
 * `ultimo` = cargado el MISMO DÍA (de Madrid) que el fichero más reciente. Lo pide así Javi: si pasan
 * seis días sin subir nada, Ops tiene que seguir viendo el tag sobre los últimos que entraron, no
 * sobre nada. Por eso la fecha de última lectura NO lleva umbral de aviso como `datosActualizadosEn`:
 * una semana sin ficheros nuevos es normal, y un aviso que salta en lo normal se aprende a ignorar.
 *
 * FORMA: la del contrato (`FicherosResponse`, BB-2180). El backend manda `ultimo` ya calculado con
 * la misma regla que aquí (`functions/src/ficheros/marcarUltimos.ts`, portada de esta); la copia del
 * front queda solo para los fixtures de `preview.html`, que no pasan por el backend.
 */

import type { FicheroLeido, FicherosResponse } from '@caes-payments/shared';

/**
 * Los tipos son los del contrato (`FicheroLeido`/`FicherosResponse`, PR #112 de Rubén). Se
 * reexportan con el nombre que usa el front para que el componente y los fixtures no dependan del
 * nombre del backend; si el contrato cambia, esto deja de compilar, que es lo que se quiere.
 */

export type { FicheroLeido };
export type FicherosLeidos = FicherosResponse;

const DIA_MADRID = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Madrid',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** `'YYYY-MM-DD'` del instante en Madrid. `en-CA` porque es el único locale que da ese orden. */
export function diaMadrid(iso: string): string {
  return DIA_MADRID.format(new Date(iso));
}

/**
 * Marca `ultimo` en los ficheros cargados el mismo día de Madrid que el más reciente, y calcula
 * `leidoEn`. Solo la usa la vista de diseño (fixtures): en la app viene calculado del backend, que
 * aplica esta misma regla. Los tests de aquí la fijan para que las dos copias no deriven.
 */
export function marcarUltimos(ficheros: ReadonlyArray<Omit<FicheroLeido, 'ultimo'>>): FicherosLeidos {
  if (ficheros.length === 0) return { leidoEn: null, ficheros: [] };
  const masReciente = ficheros.reduce((max, f) => (Date.parse(f.cargadoEn) > Date.parse(max) ? f.cargadoEn : max), ficheros[0]!.cargadoEn);
  const dia = diaMadrid(masReciente);
  return {
    leidoEn: masReciente,
    ficheros: ficheros.map((f) => ({ ...f, ultimo: diaMadrid(f.cargadoEn) === dia })),
  };
}

/** Más recientes primero; a igual instante, por nombre, para que el orden sea estable. */
export function ordenarPorCarga(ficheros: readonly FicheroLeido[]): FicheroLeido[] {
  return [...ficheros].sort(
    (a, b) => Date.parse(b.cargadoEn) - Date.parse(a.cargadoEn) || a.driveFileName.localeCompare(b.driveFileName, 'es'),
  );
}

const RELOJ_MADRID = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `dd/mm/yyyy, hh:mm` en hora de Madrid. Devuelve `null` si el ISO no se puede leer. */
export function formatearInstante(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return RELOJ_MADRID.format(new Date(ms));
}

/**
 * La línea de cabecera. Dice el hecho (cuándo entró el último fichero), no un juicio: aquí no hay
 * "dato viejo", porque no subir ficheros durante días es normal.
 */
export function describirLectura(datos: FicherosLeidos): string {
  const cuando = formatearInstante(datos.leidoEn);
  if (cuando === null) return 'Ningún fichero de certificación con viajes pendientes';
  const nUltimos = datos.ficheros.filter((f) => f.ultimo).length;
  const n = datos.ficheros.length;
  return `Última lectura de Drive: ${cuando} · ${nUltimos} de ${n} ${n === 1 ? 'fichero' : 'ficheros'} en esa carga`;
}
